import { Draft } from 'immer';
import { getType } from 'typesafe-actions';

import { findItem } from '../common/helpers';
import { createCreativeSize } from '../common/mappers';
import { ADAPTIVE_SIZE } from '../common/typings';
import * as commonCreativesActions from '../CommonCreatives/actions';
import * as html5CreativesActions from '../Html5App/actions';

import { BatchesListState } from './typings';
import { CreativesBatch, CreativesBatchItem, TriState } from './typings';

export const initialBatchesListState: BatchesListState = {
    creativesSelectorVisible: false,
    creativesSelectorDataProvider: [],
    deleteConfirmVisible: false,
    idToDelete: '',
    collection: {
        batches: [],
        total: 0
    },
    presetsUrl: ''
};

function updateBatchSelection(batch: Draft<CreativesBatch>): void {
    const items = getFitBySizeItems(batch.items);
    const total = items.length;
    const totalSelected = items.filter(item => item.selected).length;
    if (total > 0 && total === totalSelected) {
        batch.selected = TriState.TRUE;
    } else if (totalSelected === 0) {
        batch.selected = TriState.FALSE;
    } else {
        batch.selected = TriState.INDETERMINATE;
    }
}

function applyFilter(sizes: string[], draft: Draft<BatchesListState>): void {
    const batches = draft.collection.batches;
    batches.forEach(batch => {
        batch.filteredBySizeTotal = batch.items.reduce((total, item) => {
            let size: string;
            if (item.data.isAdaptive) {
                size = ADAPTIVE_SIZE;
            } else {
                size = createCreativeSize({
                    w: item.data.width,
                    h: item.data.height
                });
            }
            item.fitsSizeFilter = sizes.indexOf(size) >= 0;
            return total + (item.fitsSizeFilter ? 1 : 0);
        }, 0);

        batch.items.forEach(item => (item.selected = false));
        updateBatchSelection(batch);
    });
}

function getFitBySizeItems(items: CreativesBatchItem[]): CreativesBatchItem[] {
    return items.filter(item => item.fitsSizeFilter);
}

export function batchesListReducer(
    draft: Draft<BatchesListState>,
    action: commonCreativesActions.CommonCreativesAction | html5CreativesActions.Html5CreativesAction
): void {
    switch (action.type) {
        case getType(commonCreativesActions.loadItems):
        case getType(html5CreativesActions.loadItems): {
            if (action.offset === 0) {
                draft.collection.batches = [];
                draft.collection.total = 0;
            }

            return;
        }

        case getType(commonCreativesActions.loadItemsSuccess):
        case getType(html5CreativesActions.loadItemsSuccess): {
            draft.collection.total = action.collection.total;
            draft.collection.batches = draft.collection.batches.concat(action.collection.batches);
            applyFilter(action.sizes, draft);

            return;
        }

        case getType(commonCreativesActions.applySizesFilter):
        case getType(html5CreativesActions.applySizesFilter): {
            applyFilter(action.sizes, draft);

            return;
        }

        case getType(commonCreativesActions.setSelection):
        case getType(html5CreativesActions.setSelection): {
            const batch = findItem(action.id, draft.collection.batches);

            getFitBySizeItems(batch.items).forEach(item => (item.selected = action.selected));
            updateBatchSelection(batch);

            return;
        }

        case getType(commonCreativesActions.saveNameSuccess):
        case getType(html5CreativesActions.saveNameSuccess): {
            const item = findItem(action.id, draft.collection.batches);
            item.name = action.val;

            return;
        }

        case getType(commonCreativesActions.showDeleteConfirm):
        case getType(html5CreativesActions.showDeleteConfirm): {
            draft.deleteConfirmVisible = true;
            draft.idToDelete = action.id;

            return;
        }

        case getType(commonCreativesActions.hideDeleteConfirm):
        case getType(html5CreativesActions.hideDeleteConfirm): {
            draft.deleteConfirmVisible = false;

            return;
        }

        case getType(commonCreativesActions.deleteCreative):
        case getType(html5CreativesActions.deleteCreative): {
            draft.deleteConfirmVisible = false;

            return;
        }

        case getType(commonCreativesActions.deleteCreativeSuccess):
        case getType(html5CreativesActions.deleteCreativeSuccess): {
            draft.idToDelete = '';
            draft.collection.total--;
            draft.collection.batches = draft.collection.batches.filter(batch => batch.id !== action.id).concat();

            return;
        }

        case getType(commonCreativesActions.showCreativesSelector):
        case getType(html5CreativesActions.showCreativesSelector): {
            draft.creativesSelectorVisible = true;
            const batch = findItem(action.id, draft.collection.batches);
            draft.creativesSelectorDataProvider = batch.items;
            const fitBySizeItems = getFitBySizeItems(draft.creativesSelectorDataProvider);
            const nobodySelected = fitBySizeItems.every(item => !item.selected);
            if (nobodySelected) {
                fitBySizeItems.forEach(item => (item.selected = true));
            }

            return;
        }

        case getType(commonCreativesActions.hideCreativesSelector):
        case getType(html5CreativesActions.hideCreativesSelector): {
            draft.creativesSelectorVisible = false;
            draft.creativesSelectorDataProvider = [];

            return;
        }

        case getType(commonCreativesActions.toggleSizesSelection):
        case getType(html5CreativesActions.toggleSizesSelection): {
            draft.creativesSelectorDataProvider.forEach(item => {
                if (action.ids.includes(item.id)) {
                    item.selected = action.selected;
                }
            });

            return;
        }

        case getType(commonCreativesActions.submitSizesSelection):
        case getType(html5CreativesActions.submitSizesSelection): {
            const selectedIds = draft.creativesSelectorDataProvider.filter(item => item.selected).map(item => item.id);
            const batchId = draft.creativesSelectorDataProvider[0].batchId;
            const batch = findItem(batchId, draft.collection.batches);

            batch.items.forEach(item => (item.selected = selectedIds.includes(item.id)));
            updateBatchSelection(batch);

            return;
        }

        default:
            return;
    }
}
